import type { AccountOption } from "@app/components/account-combobox";
import KpiChart from "@app/components/kpi-chart";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import { ProvenanceBadge } from "@app/pages/transactions/provenance-badge";
import TxnTablePanel, { useTxnSort } from "@app/components/txn-table-panel";
import { useTxnBulkActions } from "@app/components/txn-table-panel/use-txn-bulk-actions";
import { useTxnPanelRefresh } from "@app/components/txn-table-panel/use-txn-panel-refresh";
import {
  DATE_RANGE_LABELS,
  dateRangeFromISO,
  useDateRange,
} from "@app/lib/date-range";
import { getPerspectiveId } from "@app/lib/perspective";
import { environment } from "@app/relay/relay-environment";
import * as Dialog from "@radix-ui/react-dialog";
import { Suspense, useEffect, useState } from "react";
import { fetchQuery, graphql, useLazyLoadQuery } from "react-relay";

import type {
  stakeholderDetailPanel_Query,
  stakeholderDetailPanel_Query$data,
} from "@app/queries/stakeholderDetailPanel_Query.graphql";
import type { stakeholderDetailPanelTxns_Query } from "@app/queries/stakeholderDetailPanelTxns_Query.graphql";

const detailQuery = graphql`
  query stakeholderDetailPanel_Query(
    $stakeholderId: ID!
    $perspectiveId: ID
    $month: String
  ) {
    stakeholderDetail(
      stakeholderId: $stakeholderId
      perspectiveId: $perspectiveId
      month: $month
    ) {
      monthly {
        month
        cashIn
        cashOut
      }
      sliceBreakdown {
        sliceType
        amount
        category {
          id
          name
          color
        }
      }
      totalCount
      limit
      txns {
        postingDate
        description
        amount
        kind
        accountNumber
        internal
        category
        topCategory
        attribution {
          source
          performedAt
          user {
            email
          }
          rule {
            regex
          }
        }
        expenseNature {
          id
          name
        }
      }
    }
  }
`;

type Detail = NonNullable<
  stakeholderDetailPanel_Query$data["stakeholderDetail"]
>;

// The editable transactions shell pulls this stakeholder's txns straight off the
// unified `transactions` resolver (counterpartyId drill), independent of the chrome
// query above, plus the option lists the shared bulk-action registry needs.
const txnsQuery = graphql`
  query stakeholderDetailPanelTxns_Query(
    $counterpartyId: ID
    $perspectiveId: ID
    $month: String
    $sort: TxnSortEnum
    $sortDirection: String
  ) {
    transactions(
      counterpartyId: $counterpartyId
      perspectiveId: $perspectiveId
      month: $month
      sort: $sort
      sortDirection: $sortDirection
    ) {
      totalCount
      totalAmount
      limit
      txns {
        txn {
          id
        }
        md5
        postingDate
        description
        amount
        kind
        accountNumber
        internal
        classification
        counterparty {
          name
        }
        category
        attribution {
          source
          performedAt
          user {
            email
          }
          rule {
            regex
          }
        }
        expenseNature {
          id
          name
        }
      }
    }
    allStakeholders {
      id
      name
    }
    bankAccounts {
      id
      name
      nickname
      last4
    }
    expenseNatures(perspectiveId: $perspectiveId) {
      id
      name
    }
  }
`;

// The plumbing nodes (Root + per-account BASE) are never valid counterparties.
const isRealStakeholder = (s: { name: string }) =>
  !s.name.startsWith("&") &&
  !s.name.toLowerCase().includes("root stakeholder") &&
  s.name !== "_root";

const money0 = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
export default function StakeholderDetailPanel({
  entry,
  perspectiveId,
  onClose,
  onOpenCategory,
  month,
  highlightCategoryId,
}: {
  entry: {
    stakeholder: { id: string; name: string };
    allocations?: ReadonlyArray<{
      id: string;
      weight: number;
      category: { id: string; name: string; color: string };
    }>;
  };
  perspectiveId: string | null;
  onClose: () => void;
  onOpenCategory?: (categoryId: string) => void;
  // When set (overview grid cell), scope to exactly this month ("YYYY-MM") and
  // ignore the global date lens. Omitted on the categorize page.
  month?: string;
  // The slice category to highlight in the breakdown (the drilled cell).
  highlightCategoryId?: string | null;
}) {
  const [detail, setDetail] = useState<Detail | null>(null);
  // The chrome query is in flight after an edit. The chart and the breakdown keep
  // rendering the pre-edit numbers until it lands, so they say so rather than
  // presenting stale totals as current.
  const [reconciling, setReconciling] = useState(false);
  const range = useDateRange();

  // Re-fetch the chrome query after the editable shell mutates txns, so the flow
  // chart and allocation breakdown reconcile with the edit (the shell owns its own
  // separate query and refresh).
  const refetchDetail = () => {
    setReconciling(true);
    fetchQuery<stakeholderDetailPanel_Query>(environment, detailQuery, {
      stakeholderId: entry.stakeholder.id,
      perspectiveId,
      month: month ?? undefined,
    }).subscribe({
      next: (data) => setDetail(data.stakeholderDetail ?? null),
      complete: () => setReconciling(false),
      error: () => setReconciling(false),
    });
  };

  useEffect(() => {
    setDetail(null);
    const subscription = fetchQuery<stakeholderDetailPanel_Query>(
      environment,
      detailQuery,
      {
        stakeholderId: entry.stakeholder.id,
        perspectiveId,
        month: month ?? undefined,
      }
    ).subscribe({
      next: (data) => setDetail(data.stakeholderDetail ?? null),
    });
    return () => subscription.unsubscribe();
  }, [entry.stakeholder.id, perspectiveId, month]);

  // Month-scoped: the server already returns just this month's txns; show that
  // single month's bar and total. Otherwise honour the global date lens.
  const cutoff = dateRangeFromISO(range);
  const monthly = month
    ? (detail?.monthly ?? []).filter((m) => m.month === month)
    : (detail?.monthly ?? []).filter((m) => !cutoff || m.month >= cutoff.slice(0, 7));
  // cashOut is the sum of negative (outflow) amounts, so cost is its negation.
  const totalCost = -monthly.reduce((sum, m) => sum + m.cashOut, 0);

  const series = [
    {
      name: "Money out",
      type: "bar" as const,
      stack: "flow",
      color: "#dc2626",
      points: monthly.map((m) => ({ month: m.month, value: m.cashOut })),
    },
    {
      name: "Money in",
      type: "bar" as const,
      stack: "flow",
      color: "#0f766e",
      points: monthly.map((m) => ({ month: m.month, value: m.cashIn })),
    },
  ];

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/20 z-40" />
        <Dialog.Content className="fixed right-0 top-0 h-full w-[680px] max-w-[92vw] bg-white shadow-2xl z-50 flex flex-col focus:outline-none">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap gap-1 mb-1">
                  {entry.allocations === undefined ? null : entry.allocations
                      .length === 0 ? (
                    <span className="text-xs text-amber-600">
                      uncategorized in this perspective
                    </span>
                  ) : (
                    entry.allocations.map((allocation) => (
                      <button
                        key={allocation.id}
                        type="button"
                        onClick={() => onOpenCategory?.(allocation.category.id)}
                        title="Open category details"
                        className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full border border-gray-200 text-gray-600 hover:border-gray-400 hover:bg-gray-50"
                      >
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: allocation.category.color }}
                        />
                        {allocation.category.name}
                        {allocation.weight < 1 &&
                          ` ${Math.round(allocation.weight * 100)}%`}
                      </button>
                    ))
                  )}
                </div>
                <Dialog.Title className="text-lg font-bold text-gray-900 truncate">
                  {entry.stakeholder.name}
                </Dialog.Title>
                <div className="text-sm text-gray-500">
                  total cost {money0(totalCost)}
                  <span className="text-gray-400">
                    {" "}
                    ·{" "}
                    {month
                      ? new Date(`${month}-01T00:00:00`).toLocaleDateString(
                          "en-US",
                          { month: "short", year: "numeric" }
                        )
                      : DATE_RANGE_LABELS[range].toLowerCase()}
                  </span>
                </div>
              </div>
              <Dialog.Close className="shrink-0 text-2xl leading-none text-gray-400 hover:text-gray-700 px-1">
                ×
              </Dialog.Close>
            </div>
          </div>

          {detail === null ? (
            <div className="p-6 text-sm text-gray-400">Loading…</div>
          ) : (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div
                className={reconciling ? "opacity-50 transition-opacity" : undefined}
                aria-busy={reconciling}
              >
                <KpiChart
                  title="Monthly flow with this counterparty"
                  series={series}
                  format="money"
                  onMonthClick={() => undefined}
                />
              </div>

              {detail.sliceBreakdown.length > 0 && (
                <section
                  className={reconciling ? "opacity-50 transition-opacity" : undefined}
                  aria-busy={reconciling}
                >
                  <h3 className="text-xs font-semibold uppercase text-gray-400 mb-2">
                    Allocation breakdown
                  </h3>
                  <div className="flex flex-col gap-1">
                    {detail.sliceBreakdown.map((slice, index) => {
                      const highlit = slice.category.id === highlightCategoryId;
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => onOpenCategory?.(slice.category.id)}
                          className={`flex items-center gap-2 text-sm rounded px-2 py-1 text-left ${
                            highlit ? "bg-emerald-50 ring-1 ring-emerald-300" : "hover:bg-gray-50"
                          }`}
                        >
                          <span
                            className="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ backgroundColor: slice.category.color }}
                          />
                          <span className="truncate text-gray-700">{slice.category.name}</span>
                          {slice.sliceType && slice.sliceType !== "PERCENTAGE" && (
                            <span className="text-[10px] uppercase text-gray-400">
                              {slice.sliceType.toLowerCase()}
                            </span>
                          )}
                          <span className="ml-auto tabular-nums text-gray-600">{money0(slice.amount)}</span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}

              <div className="bg-white rounded border overflow-hidden">
                <Suspense
                  fallback={
                    <p className="p-6 text-sm text-gray-400">
                      Loading transactions…
                    </p>
                  }
                >
                  <StakeholderTxns
                    stakeholder={entry.stakeholder}
                    month={month}
                    onChanged={refetchDetail}
                  />
                </Suspense>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// The editable transactions list for this stakeholder, fed by its own
// `transactions(counterpartyId: …)` query and wired to the shared bulk-action
// registry. The provenance badge rides along in the description cell.
function StakeholderTxns({
  stakeholder,
  month,
  onChanged,
}: {
  stakeholder: { id: string; name: string };
  // The drilled month ("YYYY-MM"), when the panel was opened from an overview
  // grid cell. The header, the chart and the allocation breakdown are all scoped
  // to it, so the table has to be too — otherwise a May cell opens a list of
  // every month's transactions under a heading that says May.
  month?: string;
  onChanged: () => void;
}) {
  const { refreshing, refresh, queryOptions } = useTxnPanelRefresh();
  const { sort, direction, onSort, sorting, sortVars } = useTxnSort();

  const data = useLazyLoadQuery<stakeholderDetailPanelTxns_Query>(
    txnsQuery,
    {
      counterpartyId: stakeholder.id,
      perspectiveId: getPerspectiveId() ?? undefined,
      month,
      ...sortVars,
    },
    queryOptions
  );
  const rows = data.transactions.txns;
  const stakeholderOptions: StakeholderOption[] =
    data.allStakeholders.filter(isRealStakeholder);
  const accountOptions: AccountOption[] = data.bankAccounts.map((a) => ({
    id: a.id,
    name: a.name,
    nickname: a.nickname,
    last4: a.last4,
  }));

  const { busy, renderBulkActions, dialogs } = useTxnBulkActions({
    enabled: ["setCounterparty", "createRule", "markInternal", "reclassify", "tagNature"],
    perspectiveId: getPerspectiveId(),
    onChanged: () => {
      refresh();
      onChanged();
    },
    rows,
    stakeholderOptions,
    accountOptions,
    natureOptions: data.expenseNatures,
  });

  const exportParams: Record<string, string> = {
    counterparty_id: stakeholder.id,
    sort: sortVars.sort,
    sort_direction: sortVars.sortDirection,
  };
  const perspectiveId = getPerspectiveId();
  if (perspectiveId) exportParams.perspective_id = perspectiveId;
  if (month) exportParams.month = month;

  return (
    <>
      <TxnTablePanel
        mode="inline"
        interaction="editable"
        title={month ? `${stakeholder.name} · ${month}` : stakeholder.name}
        totalCount={data.transactions.totalCount}
        headerTotalDollars={data.transactions.totalAmount}
        rows={rows}
        emptyLabel="No transactions for this counterparty."
        sort={sort}
        direction={direction}
        onSort={onSort} sorting={sorting}
        exportParams={exportParams}
        busy={busy || refreshing}
        renderBulkActions={renderBulkActions}
        provenance={(row) => <ProvenanceBadge attribution={row.attribution} />}
      />
      {dialogs}
    </>
  );
}
